import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useStyles = makeStyles({
  spinnerWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: designTokens.spacing[5],
    "--colorBrandStroke1": "var(--nh-spinner-accent)",
    "--colorBrandStroke2Contrast": "var(--nh-spinner-ring)",
  },
  interviewContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    padding: designTokens.spacing[5],
    paddingBottom: 0,
    gap: designTokens.spacing[3],
  },
  title: {
    color: designTokens.neutral[800],
    fontWeight: designTokens.fontWeight.semibold,
  },
  progressField: {
    flexShrink: 0,
  },
  progressFieldCompleted: {
    flexShrink: 0,
    "& .fui-ProgressBar": {
      backgroundColor: designTokens.primary[900],
    },
  },
  bottomSection: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  finishedBottomSection: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[8]}`,
    "@media (max-width: 480px)": {
      padding: `${designTokens.spacing[3]} ${designTokens.spacing[3]}`,
    },
  },
  countdownFooter: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  countdownContainer: {
    display: "flex",
    justifyContent: "center",
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    flexShrink: 0,
  },
  countdownText: {
    color: designTokens.neutral[600],
  },
  inputSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[4],
    paddingTop: designTokens.spacing[3],
    backgroundColor: designTokens.neutral.white,
    borderTop: `1px solid ${designTokens.neutral[200]}`,
    flexShrink: 0,
  },
  voiceControlsRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    flexWrap: "wrap",
  },
  customDropdown: {
    minWidth: "180px",
    borderRadius: designTokens.borderRadius.md,
  },
  questionWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    backgroundColor: designTokens.primary[50],
    borderRadius: designTokens.borderRadius.lg,
  },
  prompt: {
    color: designTokens.neutral[800],
    fontWeight: designTokens.fontWeight.medium,
  },
  noBorderField: {
    "& .fui-Field__validationMessage": {
      display: "none",
    },
  },
  inputAreaRoot: {
    width: "100%",
    minHeight: "52px",
    resize: "none",
  },
  inputArea: {
    width: "100%",
    minHeight: "52px",
    resize: "none",
  },
  textareaSlot: {
    resize: "none",
    overflow: "hidden",
  },
  errorText: {
    color: designTokens.semantic.error,
    fontSize: designTokens.fontSize.sm,
  },
  submitButton: {
    backgroundColor: designTokens.primary[900],
    color: designTokens.neutral.white,
    ":hover": {
      backgroundColor: designTokens.accent[500],
    },
  },
  submitButtonDisabled: {
    backgroundColor: designTokens.neutral[200],
    color: designTokens.neutral[500],
  },

  inputSectionWrapper: {
    backgroundColor: designTokens.neutral[50],
    paddingTop: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[8],
    paddingRight: designTokens.spacing[8],
    paddingBottom: 0,
    flexShrink: 0,
    "@media (max-width: 480px)": {
      paddingLeft: designTokens.spacing[3],
      paddingRight: designTokens.spacing[3],
    },
  },

  inputCard: {
    maxWidth: "900px",
    margin: "0 auto",
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.xl,
    boxShadow: designTokens.shadow.md,
    overflow: "hidden",
    "@media (max-width: 480px)": {
      maxWidth: "100%",
    },
  },

  tabBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "44px",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
  },

  tabGroup: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
  },

  tab: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[600],
    fontWeight: designTokens.fontWeight.normal,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[800],
    },
  },

  tabActive: {
    border: `1px solid ${designTokens.accent[500]}`,
    color: designTokens.neutral[800],
    fontWeight: designTokens.fontWeight.semibold,
  },

  tabMeta: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },

  languageSelect: {
    minWidth: "160px",
    fontSize: designTokens.fontSize.sm,
    "@media (max-width: 480px)": {
      minWidth: "unset",
      width: "120px",
    },
  },

  textPanel: {
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    "@media (max-width: 480px)": {
      padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    },
  },

  likertPrompt: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[500],
  },

  textPanelBody: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    "@media (max-width: 480px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: designTokens.spacing[2],
    },
  },

  likertActionsRow: {
    justifyContent: "flex-end",
  },

  textPanelTextareaRoot: {
    flex: "1",
    width: "100%",
    border: "none",
    boxShadow: "none",
    ":hover": {
      border: "none",
      boxShadow: "none",
    },
    ":active": {
      border: "none",
      boxShadow: "none",
    },
    ":focus-within": {
      border: "none",
      boxShadow: "none",
    },
    "::after": {
      display: "none",
    },
  },

  textPanelTextareaBusy: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  textPanelTextareaInner: {
    resize: "none",
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[800],
    lineHeight: `${designTokens.lineHeight.relaxed}`,
    outline: "none",
    ":focus": {
      outline: "none",
    },
    "::placeholder": {
      fontFamily: designTokens.fontFamily,
      fontSize: designTokens.fontSize.sm,
      color: designTokens.neutral[400],
    },
    "@media (max-width: 480px)": {
      minHeight: "44px",
    },
  },

  submitBtn: {
    width: "36px",
    height: "36px",
    minWidth: "36px",
    padding: "0",
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.accent[700],
    color: designTokens.neutral.white,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    transform: "scale(1)",
    ":hover": {
      backgroundColor: designTokens.accent[700],
      transform: "scale(1.05)",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[700]}`,
      outlineOffset: "2px",
    },
    "@media (max-width: 480px)": {
      width: "100%",
      height: "44px",
      borderRadius: designTokens.borderRadius.lg,
    },
  },

  submitBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    ":hover": {
      backgroundColor: designTokens.accent[700],
      transform: "scale(1)",
    },
  },

  skipBtn: {
    width: "36px",
    height: "36px",
    minWidth: "36px",
    padding: "0",
    border: `1px solid ${designTokens.neutral[300]}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: "transparent",
    color: designTokens.neutral[600],
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[400]}`,
      color: designTokens.neutral[800],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    "@media (max-width: 480px)": {
      width: "44px",
      height: "44px",
      borderRadius: designTokens.borderRadius.lg,
    },
  },

  skipBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    ":hover": {
      backgroundColor: "transparent",
      border: `1px solid ${designTokens.neutral[300]}`,
      color: designTokens.neutral[600],
    },
  },

  skipBtnVoice: {
    border: `1px solid ${designTokens.neutral[300]}`,
    backgroundColor: "transparent",
    color: designTokens.neutral[600],
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[400]}`,
      color: designTokens.neutral[800],
    },
    ":hover:active": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[400]}`,
      color: designTokens.neutral[800],
    },
  },

  kbdHint: {
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "right",
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[400],
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    "@media (max-width: 480px)": {
      display: "none",
    },
  },

  tabLabel: {
    "@media (max-width: 480px)": {
      display: "none",
    },
  },

  kbdHintInvisible: {
    display: "none",
  },

  voicePanel: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },

  hiddenVoiceControls: {
    display: "none",
  },

  voiceState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[2]}`,
    textAlign: "center",
  },
  voiceUnavailableWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[2]}`,
    textAlign: "center",
  },
  voiceUnavailableState: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[4],
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[2]}`,
    textAlign: "center",
    width: '420px'
  },
  voiceAvatar: {
    width: "52px",
    height: "52px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.primary[900],
    color: designTokens.primary[300],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  voiceStateInfo: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    alignItems: "center",
    textAlign: "center",
  },

  voiceDisabledStateInfo: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    alignItems: "flex-start",
    textAlign: "left",
  },

  voiceStateStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[2],
  },

  voiceStateDot: {
    width: "8px",
    height: "8px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.accent[500],
    flexShrink: 0,
  },

  voiceStateDotPaused: {
    width: "8px",
    height: "8px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.neutral[400],
    flexShrink: 0,
  },

  voiceStateSublabel: {
    color: designTokens.neutral[500],
  },

  voiceStateButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[2],
    flexWrap: "wrap",
    "& button": {
      height: "36px",
    },
  },

  compactFooter: {
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    "@media (max-width: 768px)": {
      paddingTop: designTokens.spacing[3],
      paddingBottom: designTokens.spacing[3],
      paddingLeft: designTokens.spacing[4],
      paddingRight: designTokens.spacing[4],
    },
  },
});

export const useLayoutStyles = makeStyles({
  page: {
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: designTokens.neutral[50],
  },
  interviewLayout: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  mainArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  historyArea: {
    flex: 1,
    overflowY: "auto",
    scrollBehavior: "smooth",
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[8]}`,
    backgroundColor: designTokens.neutral[50],
    "@media (max-width: 480px)": {
      padding: `${designTokens.spacing[3]} ${designTokens.spacing[3]}`,
    },
  },
  inputArea: {
    flexShrink: 0,
  },
  cardJustifyItemsCenter: {
    alignItems: "center",
  },
  smallGrayHeader: {
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.sm,
    justifyContent: "center",
    textAlign: "center",
  },
  centerFullWidth: {
    width: "100%",
    textAlign: "center",
  },

  sidebarInDrawer: {
    borderRight: "none",
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },

  sidebarToggleBar: {
    display: "none",
    "@media (max-width: 768px)": {
      display: "flex",
      alignItems: "center",
      padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
      borderBottom: `1px solid ${designTokens.neutral[200]}`,
      backgroundColor: designTokens.neutral.white,
      flexShrink: 0,
    },
  },

  sidebarToggleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    height: "32px",
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    background: "transparent",
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[500],
    fontWeight: designTokens.fontWeight.medium,
    transition: `background ${designTokens.transition.fast}, color ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[700],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },

  sidebarDrawer: {
    width: designTokens.drawerWidth.mobile,
  },

  sidebarDrawerBody: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
  },
});
